// where the TimeSlot component show color based on their states, and changes, sends request when clicked

import { useState, useEffect } from "react";
import CalendarTitleCell from "./CalendarTitleCell";
import CalendarRow from "./CalendarRow";

import uniqid from "uniqid";
import { getDayStr } from "../../../functions/formatTime";
import { DateTime } from "luxon";
import {
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Divider,
} from "@chakra-ui/react";
import { getPurposeStr } from "../../../functions/reservation";
import color from "../../../config/color";
import { fetchWithToken } from "../../../functions/fetch";

const Calendar = ({ reservations, timeSlots, setRefetchReservation }) => {
  const [dateRow, setDateRow] = useState([]);
  const [timeLabelList, setTimeLabelList] = useState([]); // first column, label of time
  const [deletingReservation, setDeletingReservation] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure(); // for confirm delete reservation modal
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dayToMs = (day) => day * 24 * 60 * 60 * 1000;

  useEffect(() => {
    // get recent 14 days first render
    const dates = [];
    for (let i = 0; i < 14; i++) {
      // iterate over the next 14 days
      const date = new Date(Date.now() + dayToMs(i)); // create a date object for the current day
      dates.push(date); // add the date object to the array
    }

    setDateRow(dates);

    // calculate time labels
    const startTime = DateTime.fromISO("08:00", { zone: "utc" });
    const endTime = DateTime.fromISO("17:00", { zone: "utc" });
    const timeSlots = [];

    let currentTime = startTime;
    while (currentTime < endTime) {
      const nextTime = currentTime.plus({ minutes: 30 });
      timeSlots.push(
        `${currentTime.toFormat("HH:mm")} - ${nextTime.toFormat("HH:mm")}`,
      );
      currentTime = nextTime;
    }

    setTimeLabelList(timeSlots);
  }, []);

  const formatJsDate = (date) => DateTime.fromJSDate(date).toFormat("MM/dd");

  const confirmDelete = async () => {
    setIsSubmitting(true);

    // call api
    const { startTime } = deletingReservation;
    const dt = DateTime.fromISO(startTime).setZone("utc+8");
    const date = dt.startOf("day").toISO();
    const hour = dt.hour + dt.minute / 60;

    const formBody = {
      date,
      hour,
      canBeReserved: "0",
    };

    try {
      const res = await fetchWithToken(
        "PUT",
        `${process.env.REACT_APP_BACKEND_HOST}/api/time-slot`,
        formBody,
      );
      if (res.status === 200) {
        setDeletingReservation(null);
        setRefetchReservation(true);
      }
    } catch (err) {
      console.error(err);
    }
    setIsSubmitting(false);
    onClose();
  };

  const closePopUp = () => {
    // refetch because showing modal caused re-render, localReservable will become old value again
    setRefetchReservation(true);
    onClose();
  };

  return (
    <div>
      {/* modal for confirm delete reservation */}
      {deletingReservation && (
        <Modal
          isOpen={isOpen}
          onClose={closePopUp}
          isCentered
          scrollBehavior="inside"
          size="lg"
        >
          <ModalOverlay />
          <ModalContent borderRadius="none">
            <ModalHeader bgColor={color.red} color="white" fontSize="2xl">
              關閉時段
            </ModalHeader>
            <ModalBody>
              <Text>關閉此時段將會刪除預約：</Text>
              <br />
              <Text>房號：{deletingReservation?.roomNumber}</Text>
              <Text>
                時段：
                {DateTime.fromISO(deletingReservation?.startTime)
                  .setZone("utc+8")
                  .toFormat("yyyy/MM/dd HH:mm")}
              </Text>
              <Text>目的：{getPurposeStr(deletingReservation?.purpose)}</Text>
              <Divider borderColor={color.red} my={2} />
              <Text>
                預約者姓名：{deletingReservation?.userId?.name || "無"}
              </Text>
              <Text>
                預約者電話：{deletingReservation?.userId?.phoneNumber || "無"}
              </Text>
              <Text>使用者備註：{deletingReservation?.userNote || "無"}</Text>
              <Text>備註：{deletingReservation?.note || "無"}</Text>
            </ModalBody>
            <ModalFooter borderRadius="none">
              <Button
                borderRadius="full"
                px={6}
                fontSize="sm"
                bgColor={color.red}
                colorScheme="red"
                mr={3}
                type="submit"
                isLoading={isSubmitting}
                onClick={confirmDelete}
              >
                確認
              </Button>
              <Button
                borderRadius="full"
                px={6}
                fontSize="sm"
                colorScheme="gray"
                bgColor={color.gray}
                color="white"
                onClick={closePopUp}
              >
                取消
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <div className="border-t-2 w-full" />
      <div className="grid grid-cols-[repeat(6,min-content)] mt-4 ml-12 font-chinese text-custom-gray whitespace-nowrap items-center gap-x-2">
        <div className="border-2 bg-custom-dark-gray w-4 h-4" />
        <p>不可預約</p>
        <div className="border-2 bg-white w-4 h-4" />
        <p>可預約</p>
        <div className="border-2 bg-custom-orange w-4 h-4" />
        <p>已預約</p>
      </div>
      <div className="border-2 mt-4 mx-12 grid grid-cols-[min-content_repeat(14,1fr)] auto-rows-min bg-light-border-gray gap-x-0.5 gap-y-0.5">
        {/* placeholder */}
        <div className="bg-white row-span-2" />
        {
          // title1: day of week
          dateRow.map((date) => (
            <CalendarTitleCell key={uniqid()} text={getDayStr(date)} />
          ))
        }
        {
          // title2: date
          dateRow.map((date) => (
            <CalendarTitleCell key={uniqid()} text={formatJsDate(date)} />
          ))
        }
        {
          // rows of timeSlot
          timeLabelList.map((text) => (
            <CalendarRow
              key={uniqid()}
              reservations={reservations.filter((res) => !res.deleted)}
              timeText={text}
              timeSlots={timeSlots}
              onOpen={onOpen}
              setDeletingReservation={setDeletingReservation}
            />
          ))
        }
      </div>
    </div>
  );
};

export default Calendar;
